import {
  differenceInCalendarMonths,
  differenceInMinutes,
  differenceInCalendarDays,
  addMonths,
  format,
  addDays,
} from "date-fns";

export const LUXE_GIFT_ACCOUNT_TAG = "luxe_gift_redeemed";
export const LUXE_GIFT_LINE_ITEM_ATTRIBUTE_KEY = "_luxe_subscription_free_gift";
const MONTHS_UNTIL_LUXE_GIFT_AVAILABLE = 2; // how many months until the gift is unlocked from membership start
const LUXE_GIFT_AVAILABILITY_DURATION = 1; // how many months the gift is available for after unlocking

export function getLuxeGiftInfo(luxe, accountTags = []) {
  const overrideMembershipStartDateTag = accountTags.find((tag) =>
    tag.startsWith("override-luxe-gift-membership-date")
  );
  const overrideMembershipStartDate = overrideMembershipStartDateTag
    ? overrideMembershipStartDateTag.split("override-luxe-gift-membership-date:")[1]
    : "";
  const membershipStartDate = new Date(
    overrideMembershipStartDate || luxe?.customerSubscription?.member_since
  ); // format: "YYYY-MM-DD"

  if (!membershipStartDate || Number.isNaN(membershipStartDate.getTime())) {
    return null;
  }

  const hasGiftAlreadyBeenRedeemed = accountTags?.some((tag) => tag === LUXE_GIFT_ACCOUNT_TAG);
  const now = new Date();
  const lockedUntil = addMonths(membershipStartDate, MONTHS_UNTIL_LUXE_GIFT_AVAILABLE);
  const unlockedUntil = addMonths(
    membershipStartDate,
    MONTHS_UNTIL_LUXE_GIFT_AVAILABLE + LUXE_GIFT_AVAILABILITY_DURATION
  );
  const isLocked = now < lockedUntil;
  const isUnlocked = now >= lockedUntil && now <= unlockedUntil;
  let status = "expired";
  let endDate = null;

  if (isLocked) {
    status = "locked";
    endDate = lockedUntil;
  } else if (isUnlocked) {
    status = "unlocked";
    endDate = unlockedUntil;
  }

  if (hasGiftAlreadyBeenRedeemed) {
    status = "redeemed";
    endDate = null;
  }

  let monthsElapsed = differenceInCalendarMonths(now, membershipStartDate);
  // differenceInCalendarMonths is rounded but we want the round down value to calculate the month progress accurately
  if (now < addMonths(membershipStartDate, monthsElapsed)) {
    monthsElapsed -= 1;
  }
  const startOfNextMembershipMonth = addMonths(membershipStartDate, monthsElapsed + 1);
  const startOfCurrentMembershipMonth = addMonths(membershipStartDate, monthsElapsed);
  const totalDaysInCurrentMonth = differenceInCalendarDays(
    startOfNextMembershipMonth,
    startOfCurrentMembershipMonth
  );
  let daysElapsedInCurrentMonth = differenceInCalendarDays(now, startOfCurrentMembershipMonth);
  // Similar to months, we want to round down the days elapsed in the current month
  if (now < addDays(startOfCurrentMembershipMonth, daysElapsedInCurrentMonth)) {
    daysElapsedInCurrentMonth -= 1;
  }
  const monthProgress = daysElapsedInCurrentMonth / totalDaysInCurrentMonth;
  const exactMonths = monthsElapsed + monthProgress;

  return {
    status,
    endDate,
    exactMonths: Math.min(
      exactMonths,
      MONTHS_UNTIL_LUXE_GIFT_AVAILABLE + LUXE_GIFT_AVAILABILITY_DURATION + 1
    ), // precise progress in decimal months - capping at 1 month after gift expires (for progress bar display purposes)
    isMonth1Complete: exactMonths >= 1,
    isGiftUnlocked: exactMonths >= MONTHS_UNTIL_LUXE_GIFT_AVAILABLE,
    isCountdownStarted:
      exactMonths >= MONTHS_UNTIL_LUXE_GIFT_AVAILABLE + LUXE_GIFT_AVAILABILITY_DURATION,
    expiryMonthLabel: format(unlockedUntil, "MMMM do"),
  };
}

export function getTimeUntil(targetDate, type) {
  const now = new Date();
  const months = differenceInCalendarMonths(targetDate, now);
  if (months >= 1) {
    const monthsString = months === 1 ? "month" : "months";
    return { formatted: `${months} ${monthsString}`, isMinutes: false };
  }

  const days = differenceInCalendarDays(targetDate, now);
  const daysString = days === 1 ? "day" : "days";
  if (days > 1) {
    return { formatted: `${days} ${daysString}`, isMinutes: false };
  }

  if (type === "eligibility") {
    return { formatted: "1 day", isMinutes: false };
  }

  if (type === "expiration") {
    const minutes = differenceInMinutes(targetDate, now);
    return { formatted: `${minutes}`, isMinutes: true };
  }

  // Default return if none of the above conditions are met
  return { formatted: "", isMinutes: false };
}

export function getLuxeContent(items, isLuxe) {
  const hasAll = items.some((i) => i.isLuxeContent === "All");

  if (hasAll) {
    return items.filter((i) => i.isLuxeContent === "All");
  }

  return items.filter((i) => i.isLuxeContent === (isLuxe ? "Yes" : "No"));
}
